/**
 * THE BOOT VEIL DROPS ON THE FIRST REAL SURFACE, NEVER BEFORE IT.
 *
 * `#app-loading` is the logo-and-spinner every visitor sees first. It used to
 * be faded the moment the auth decision arrived — at the TOP of
 * `proceedToLobby` and `handleSignedOutBoot` — while the surface the player
 * was actually going to use did not exist yet. Measured with a per-frame
 * recorder on the real backend (tests/e2e-playwright/real/boot-timeline.real.spec.js):
 *
 *   reload mid-tutorial   veil off 615ms → LOBBY 749ms → lobby gone, board 953ms
 *   warm reload, lobby    veil off 651ms → nothing → lobby 818ms
 *   production first visit veil off 2738ms → NOTHING for 1.2s → board 3976ms
 *
 * That is the report: "I visit a page and it keeps morphing … the lobby and
 * then the tutorial rendered on top of that." A loading screen that leaves
 * before the load is done is not a loading screen.
 *
 * So the veil now stays until a destination announces itself:
 *   - the lobby, once `proceedToLobby` has built it and the socket has had
 *     its chance to claim a mid-match resume (`reveal`);
 *   - the sign-in card, the consent gate, the username onboarding, or a game
 *     view — each a final destination, so each drops the veil outright (`drop`).
 * Two boot paths install their real surface only after a server round-trip
 * and register a HOLD for it: a pending game resume (the board replaces the
 * lobby a beat later) and the guest tutorial start. Every hold is capped, and
 * a backstop drops the veil regardless: a gate that can fail to open has
 * deleted the feature it was guarding (docs/asset-loading.md, law 2).
 *
 * The invitation arrival keeps its own hold (`data-invite-pending`), exactly
 * as `main.js` carried it before: the fade waits for `moonfall:invitesettled`
 * or 10.5s, whichever comes first.
 *
 * Leaf module on purpose: no imports, so `lobby.js`, `consent-gate.js` and
 * `socket-handler.js` can all reach it without a new edge into `main.js`.
 */
const BootVeil = (function () {
    'use strict';

    const FADE_MS = 300;
    /* Nothing on the boot path is allowed to take longer than this without
       showing SOMETHING. Long enough for a cold production first visit (the
       guest room is built server-side, measured ~1.3s after the veil used to
       drop), far shorter than a player's patience. */
    const BACKSTOP_MS = 12000;
    const INVITE_SETTLE_CAP_MS = 10500;

    let released = false;
    let revealRequested = false;
    /* Background warms (store art, the Exchange config, the invite ticket)
       wait for this: while the veil is up the link belongs to what the first
       screen needs, and on a slow phone a megabyte of speculative art racing
       the lobby's own reads is what makes the lobby late. */
    let announceReleased = null;
    let releasedPromise = new Promise((resolve) => { announceReleased = resolve; });
    const holds = new Map();
    let backstopTimer = null;
    let backstopDueAt = 0;

    function element() {
        return document.getElementById('app-loading');
    }

    function isUp() {
        return !released && Boolean(element());
    }

    function fade() {
        const veil = element();
        if (!veil) return;
        veil.classList.add('fade-out');
        setTimeout(() => { veil.remove(); }, FADE_MS);
    }

    /*
     * AN INVITED ARRIVAL SEES ONE SCREEN, NOT TWO.
     *
     * Confirming an invite code costs a round-trip, and the app does not wait
     * for it -- so the veil dropped, "Sign in to Play" painted, and the ticket
     * landed on top of it a beat later. `invite-ticket.js` sets this flag while
     * a code is unresolved and clears it the moment the finished ticket is on
     * screen, or as soon as it knows no ticket is coming. The timeout is a
     * backstop over ITS backstop: this holds the entire app behind a loading
     * screen, so it must never be able to stick.
     */
    function fadeWhenInviteSettles() {
        if (document.documentElement?.dataset?.invitePending !== '1') {
            fade();
            return;
        }
        let done = false;
        const settle = () => {
            if (done) return;
            done = true;
            fade();
        };
        window.addEventListener('moonfall:invitesettled', settle, { once: true });
        setTimeout(settle, INVITE_SETTLE_CAP_MS);
    }

    function finish(reason) {
        if (released) return false;
        released = true;
        for (const timer of holds.values()) clearTimeout(timer);
        holds.clear();
        clearTimeout(backstopTimer);
        backstopTimer = null;
        if (window.CLIENT_DEBUG_MODE) console.log('[BootVeil] released:', reason);
        fadeWhenInviteSettles();
        if (announceReleased) announceReleased(reason);
        return true;
    }

    /**
     * The surface under the veil is ready. Fades now unless a hold is pending,
     * in which case the fade happens when the last hold settles or caps.
     */
    function reveal(reason) {
        if (released) return false;
        if (holds.size > 0) {
            revealRequested = true;
            return false;
        }
        return finish(reason);
    }

    /** A final destination is on screen: drop the veil, holds included. */
    function drop(reason) {
        return finish(reason);
    }

    /**
     * A final destination is INSTALLED under the veil but still painting (a
     * board whose card backs, portraits and hand are decoding). Every
     * boot-phase hold is moot now; the veil fades when `presented` settles, or
     * at `capMs`. It is raised over the game chrome that `game-active` lifts
     * above the boot layer, so nothing peeks through it early.
     */
    function arrive(reason, presented, capMs) {
        if (released) return false;
        for (const timer of holds.values()) clearTimeout(timer);
        holds.clear();
        element()?.classList.add('boot-veil--arrival');
        const settle = hold(reason, capMs);
        revealRequested = true;
        Promise.resolve(presented).then(settle, settle);
        return true;
    }

    /**
     * Keep the veil up while `reason` installs its surface. Returns the
     * settle function; the cap settles it on its own.
     */
    function hold(reason, capMs) {
        if (released) return () => false;
        const settle = () => {
            const timer = holds.get(reason);
            if (timer === undefined) return false;
            clearTimeout(timer);
            holds.delete(reason);
            if (holds.size === 0 && revealRequested) finish(`${reason}:settled`);
            return true;
        };
        if (holds.has(reason)) clearTimeout(holds.get(reason));
        holds.set(reason, setTimeout(() => {
            holds.delete(reason);
            /* A capped hold reveals whatever is underneath rather than waiting
               on a surface that may never come — once no OTHER hold is still
               pending. A cap releases its own hold only: the lobby's 3 s
               arrival cap used to finish the veil over a mid-match reload's
               4 s resume hold, so a resume answering at 3-4 s showed the
               lobby, then the loading screen, then the board (25 September
               2026). The remaining hold ends at its own cap, or when its
               surface lands; the backstop still bounds all of them. */
            revealRequested = true;
            if (holds.size === 0) finish(`${reason}:cap`);
        }, Math.max(0, Number(capMs) || 0)));
        return settle;
    }

    /**
     * Settle one hold by its reason, from a caller that does not own its
     * settle function (a refused resume: the lobby is the destination now,
     * and the lobby's own arrival hold decides when the veil lifts).
     * @returns {boolean} whether that hold was pending
     */
    function settle(reason) {
        const timer = holds.get(reason);
        if (timer === undefined) return false;
        clearTimeout(timer);
        holds.delete(reason);
        if (holds.size === 0 && revealRequested) finish(`${reason}:settled`);
        return true;
    }

    function armBackstop() {
        if (released || backstopTimer !== null || !element()) return;
        backstopDueAt = Date.now() + BACKSTOP_MS;
        backstopTimer = setTimeout(() => finish('backstop'), BACKSTOP_MS);
    }

    /**
     * A HOLD WHOSE WAIT BEGINS LATE. The guest tutorial's start asks for the
     * connection once its pass is back, and the connection may take its whole
     * 10 s from then. Measured, 14 October 2026: with the pass back at 3 s and
     * the language file slow too (so the start was the first to ask), the
     * failure card arrived at 12.9-13.0 s, after the backstop armed at boot had
     * faded the veil onto an empty page for 270-290 ms (Chrome, Firefox and
     * WebKit; tests/e2e-playwright/connect-retry.spec.js).
     *
     * `stretch` gives a pending hold its cap again from now, and keeps the
     * backstop from firing before that cap. Still bounded: a new cap, never an
     * open wait. Nothing happens for a hold that is not pending or once the
     * veil has gone.
     */
    function stretch(reason, capMs) {
        if (released || !holds.has(reason)) return false;
        const cap = Math.max(0, Number(capMs) || 0);
        hold(reason, cap);
        if (backstopTimer !== null && Date.now() + cap > backstopDueAt) {
            clearTimeout(backstopTimer);
            backstopDueAt = Date.now() + cap;
            backstopTimer = setTimeout(() => finish('backstop'), cap);
        }
        return true;
    }

    /** Resolves once the veil has been released (never rejects). A page with
        no veil at all has nothing to wait for. */
    function whenReleased() {
        if (released || !element()) return Promise.resolve();
        return releasedPromise;
    }

    /** Test seam: a fresh page, a fresh veil. */
    function reset() {
        released = false;
        revealRequested = false;
        releasedPromise = new Promise((resolve) => { announceReleased = resolve; });
        for (const timer of holds.values()) clearTimeout(timer);
        holds.clear();
        clearTimeout(backstopTimer);
        backstopTimer = null;
        backstopDueAt = 0;
    }

    return {
        BACKSTOP_MS,
        FADE_MS,
        armBackstop,
        arrive,
        drop,
        hold,
        isUp,
        reset,
        reveal,
        settle,
        stretch,
        whenReleased,
    };
})();

export { BootVeil };
